export type Country = {
  name: string;
  code: string;
  dial_code: string;
  flag: string;
};

export const Countries: Country[] = [
  { name: "Kuwait", code: "KW", dial_code: "+965", flag: "https://flagcdn.com/w40/kw.png" },
  { name: "Saudi Arabia", code: "SA", dial_code: "+966", flag: "https://flagcdn.com/w40/sa.png" },
  { name: "United Arab Emirates", code: "AE", dial_code: "+971", flag: "https://flagcdn.com/w40/ae.png" },
  { name: "Bahrain", code: "BH", dial_code: "+973", flag: "https://flagcdn.com/w40/bh.png" },
  { name: "Qatar", code: "QA", dial_code: "+974", flag: "https://flagcdn.com/w40/qa.png" },
  { name: "Oman", code: "OM", dial_code: "+968", flag: "https://flagcdn.com/w40/om.png" },
  { name: "Pakistan", code: "PK", dial_code: "+92", flag: "https://flagcdn.com/w40/pk.png" },
];

export const countryCodeToDialCode: Record<string, string> = {
  PK: "92",
  KW: "965",
  SA: "966",
  BH: "973",
  QA: "974",
  AE: "971",
  OM: "968",
};

type PhonePattern = {
  min: number;
  max: number;
  mask: string;
  regex: RegExp;
};

export const phonePatterns: Record<string, PhonePattern> = {
  KW: { min: 8, max: 8, mask: "99999999", regex: /^[569]\d{7}$/ },
  SA: { min: 9, max: 9, mask: "999999999", regex: /^5\d{8}$/ },
  AE: { min: 9, max: 9, mask: "999999999", regex: /^(50|52|54|55|56|58)\d{7}$/ },
  BH: { min: 8, max: 8, mask: "99999999", regex: /^3\d{7}$/ },
  QA: { min: 8, max: 8, mask: "99999999", regex: /^[3567]\d{7}$/ },
  OM: { min: 8, max: 8, mask: "99999999", regex: /^[79]\d{7}$/ },
  PK: { min: 10, max: 10, mask: "9999999999", regex: /^3\d{9}$/ },
};

const defaultPattern: PhonePattern = {
  min: 8,
  max: 10,
  mask: "9999999999",
  regex: /^\d{8,10}$/,
};

export const getMaxDigits = (countryCode: string): number =>
  (phonePatterns[countryCode] || defaultPattern).max;

export const getMaskPattern = (countryCode: string): string => {
  const dialCode = countryCodeToDialCode[countryCode] || "965";
  switch (dialCode) {
    case "92":
      return "9999999999";
    case "965":
      return "99999999";
    case "966":
      return "999999999";
    case "973":
      return "99999999";
    case "974":
      return "99999999";
    case "971":
      return "999999999";
    case "968":
      return "99999999";
    default:
      return "9999999999";
  }
};

export const validatePhoneNumber = (
  phoneNumber: string,
  countryCode: string
): { isValid: boolean; error: string } => {
  const digits = (phoneNumber || "").replace(/\D/g, "");
  const pattern = phonePatterns[countryCode] || defaultPattern;

  if (!digits) {
    return { isValid: false, error: "phone-required" };
  }
  if (digits.length < pattern.min) {
    return { isValid: false, error: "phone-min" };
  }
  if (digits.length > pattern.max) {
    return { isValid: false, error: "phone-max" };
  }
  if (!pattern.regex.test(digits)) {
    return { isValid: false, error: "phone-invalid" };
  }
  return { isValid: true, error: "" };
};

export const getCountryByDialCode = (dialDigits: string): Country => {
  const code = dialDigits?.startsWith("+") ? dialDigits : `+${dialDigits}`;
  return Countries.find((c) => c.dial_code === code) || Countries[0];
};

export const getCountryByCode = (countryCode?: string): Country =>
  Countries.find((c) => c.code === countryCode) || Countries[0];
